import React, { useState, useEffect } from 'react'
import SearchBar from '../components/SearchBar'
import Newsletter from '../components/Newsletter'
import CorrespondingCodes from '../components/CorrespondingCodes'
import CodeCard from '../components/CodeCard'
import Faq from '../components/Faq'
import supabase from '../services/supabase'
import '../styles/global.css'

const CorrespondenceCnaePage = () => {
  const [selectedCode, setSelectedCode] = useState(null)
  const [correspondingCodes, setCorrespondingCodes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [popularCodes, setPopularCodes] = useState([])
  const [isLoadingPopular, setIsLoadingPopular] = useState(true)
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  // FAQ data for the page
  const faqData = [
    {
      question: '¿Qué es la correspondencia entre CNAE 2025 y CNAE 2009?',
      answer:
        'La correspondencia entre CNAE 2025 y CNAE 2009 es una herramienta que permite relacionar los códigos de ambas clasificaciones. Esta correspondencia es fundamental para las empresas que necesitan actualizar sus códigos CNAE de la versión 2009 a la nueva versión 2025, facilitando la transición entre ambas clasificaciones y asegurando la continuidad en los registros estadísticos y administrativos.',
    },
    {
      question:
        '¿Por qué es importante conocer la correspondencia entre CNAE 2025 y CNAE 2009?',
      answer: {
        intro:
          'Conocer la correspondencia entre ambas clasificaciones es importante por varios motivos:',
        bullets: [
          'Facilita la actualización de los registros empresariales al nuevo sistema CNAE 2025',
          'Permite comparar datos estadísticos entre diferentes periodos',
          'Ayuda a cumplir correctamente con las obligaciones fiscales y administrativas',
          'Evita errores en la clasificación de actividades económicas durante el periodo de transición',
        ],
      },
    },
    {
      question:
        '¿Cómo puedo encontrar el código CNAE 2025 que corresponde a mi código CNAE 2009?',
      answer:
        'Para encontrar el código CNAE 2025 que corresponde a tu código CNAE 2009, simplemente utiliza nuestra herramienta de correspondencia. Introduce tu código CNAE 2009 en el buscador, selecciónalo de la lista de resultados y presiona el botón "Encontrar correspondencia". La herramienta te mostrará automáticamente los códigos CNAE 2025 correspondientes con sus descripciones detalladas.',
    },
    {
      question:
        '¿Un código CNAE 2009 puede corresponder a varios códigos CNAE 2025?',
      answer:
        'Sí, es posible que un código CNAE 2009 corresponda a varios códigos CNAE 2025. Esto se debe a que la nueva clasificación CNAE 2025 es más detallada en algunas actividades económicas, dividiendo categorías que antes estaban agrupadas. Por ello, algunas actividades que en la CNAE 2009 estaban bajo un mismo código, ahora pueden estar distribuidas en varios códigos diferentes en la CNAE 2025.',
    },
    {
      question:
        '¿Qué debo hacer si mi actividad no tiene una correspondencia exacta entre CNAE 2009 y CNAE 2025?',
      answer:
        'Si tu actividad no tiene una correspondencia exacta, debes analizar las descripciones detalladas de los códigos CNAE 2025 sugeridos y seleccionar el que mejor se ajuste a tu actividad principal. En caso de duda, puedes consultar con un asesor fiscal o directamente con la Agencia Tributaria para asegurarte de elegir el código correcto.',
    },
    {
      question: '¿Hasta cuándo puedo seguir utilizando mi código CNAE 2009?',
      answer:
        'Las empresas deben actualizar su código CNAE a la versión 2025 a más tardar el 30 de junio de 2025. Este plazo se establece para que las empresas puedan adaptar sus registros a la nueva clasificación antes de que se implemente de manera definitiva en los procesos administrativos y fiscales.',
    },
    {
      question:
        '¿Qué cambios principales introduce la CNAE 2025 respecto a la CNAE 2009?',
      answer: {
        intro:
          'Los principales cambios que introduce la CNAE 2025 respecto a la CNAE 2009 son:',
        bullets: [
          'Mayor número de clases: La CNAE 2025 incluye 664 clases, en comparación con las 629 clases de la CNAE 2009',
          'Nuevas categorías para actividades emergentes como comercio electrónico, economía digital y energías renovables',
          'Reestructuración de algunas divisiones para reflejar mejor la realidad económica actual',
          'Mayor detalle en la clasificación de servicios tecnológicos y actividades relacionadas con la sostenibilidad',
        ],
      },
    },
    {
      question:
        '¿Cómo afecta la actualización de CNAE 2009 a CNAE 2025 a mis obligaciones fiscales?',
      answer:
        'La actualización de tu código CNAE no modifica por sí misma tus obligaciones fiscales, pero es importante tener el código correcto para asegurar que cumples adecuadamente con todas tus obligaciones. Un código CNAE incorrecto podría llevar a inconsistencias en tus declaraciones fiscales o a la aplicación incorrecta de ciertos beneficios fiscales específicos para determinadas actividades.',
    },
  ]

  // Fetch popular CNAE 2009 codes with pagination
  const fetchPopularCodes = async (
    limitParam = limit,
    offsetParam = offset,
    append = false
  ) => {
    try {
      setIsLoadingPopular(true)
      const { data, error } = await supabase.rpc(
        'get_most_searched_cnae_2009_codes',
        {
          limit_param: limitParam,
          offset_param: offsetParam,
        }
      )

      if (error) throw error

      if (!data || data.length === 0) {
        console.warn('No popular CNAE 2009 codes found')
        setHasMore(false)
        if (!append) {
          setPopularCodes([])
        }
        return
      }

      // If we're appending, add to existing codes, otherwise replace
      if (append) {
        setPopularCodes((prevCodes) => [...prevCodes, ...data])
      } else {
        setPopularCodes(data)
      }

      // Check if we have more data to load
      setHasMore(data.length === limitParam)
    } catch (error) {
      console.error('Error fetching popular CNAE 2009 codes:', error)
      setError('Error al cargar los códigos CNAE 2009 populares')
    } finally {
      setIsLoadingPopular(false)
    }
  }

  // Load more codes
  const handleLoadMore = () => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchPopularCodes(limit, newOffset, true)
  }

  useEffect(() => {
    fetchPopularCodes()
  }, [])

  // Handle code selection from search bar
  const handleCodeSelect = (code) => {
    setSelectedCode(code)
    setCorrespondingCodes([]) // Clear previous results
  }

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-16 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Correspondencia entre códigos CNAE 2025 y CNAE 2009
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Encuentra fácilmente las correspondencias entre los códigos CNAE 2025
          y CNAE 2009. Busca por código o descripción y obtén información
          detallada sobre las equivalencias entre ambas clasificaciones.
        </p>

        {/* Search Bar with Correspondence Button */}
        <div className='max-w-xl mx-auto mb-6'>
          <SearchBar
            type='both-cnae'
            hideConvertButton={true}
            hideCorrespondenceButton={false}
            onCodeSelect={handleCodeSelect}
            onFindCorrespondence={(codes, sourceType, sourceCode) => {
              console.log('[DEBUG] onFindCorrespondence callback called')
              console.log('[DEBUG] Received codes:', codes)
              console.log('[DEBUG] Source type:', sourceType)
              console.log('[DEBUG] Source code:', sourceCode)

              setCorrespondingCodes(codes)
              console.log('[DEBUG] Set correspondingCodes state to:', codes)

              setIsLoading(false)
              console.log('[DEBUG] Set isLoading state to false')

              // If no corresponding codes were found, set an error message
              if (!codes || codes.length === 0) {
                console.log('[DEBUG] No corresponding codes found')
                const sourceTypeText =
                  sourceType === 'cnae' ? 'CNAE 2025' : 'CNAE 2009'
                const targetTypeText =
                  sourceType === 'cnae' ? 'CNAE 2009' : 'CNAE 2025'
                const errorMessage = `No se encontraron códigos ${targetTypeText} correspondientes a este código ${sourceTypeText}.`
                console.log('[DEBUG] Setting error message:', errorMessage)
                setError(errorMessage)
              } else {
                // Clear any previous error
                console.log('[DEBUG] Clearing previous error')
                setError(null)
              }
            }}
            error={error}
            setError={setError}
            setIsLoading={setIsLoading}
          />
        </div>
      </header>

      {/* Error Message */}
      {error && (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6'>
          {error}
        </div>
      )}

      {/* Loading Indicator - only show if we're loading and there's no error */}
      {isLoading && !error && (
        <div className='flex justify-center my-8'>
          <div className='w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
        </div>
      )}

      {/* Corresponding Codes Section */}
      {correspondingCodes.length > 0 && (
        <CorrespondingCodes
          correspondingCodes={correspondingCodes}
          sourceType={selectedCode.type}
          sourceCode={selectedCode.code}
        />
      )}

      {/* Popular CNAE 2009 Codes Section */}
      <section className='py-8 md:py-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-6 text-text-primary text-center'>
          Códigos CNAE 2009 Más Populares
        </h2>
        {isLoadingPopular && popularCodes.length === 0 ? (
          <div className='flex justify-center'>
            <div className='w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
          </div>
        ) : popularCodes.length > 0 ? (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {popularCodes.map((code, index) => (
                <CodeCard
                  key={index}
                  code={code.cod_cnae_2009}
                  title={code.titulo_cnae_2009}
                  type='cnae2009'
                  slug={code.slug_cnae_2009}
                  searchCount={code.cant_busquedas_cnae_2009}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className='flex justify-center mt-8'>
                <button
                  onClick={handleLoadMore}
                  className='px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors duration-300 flex items-center'
                  disabled={isLoadingPopular}
                >
                  {isLoadingPopular ? (
                    <>
                      <span className='mr-2'>Cargando...</span>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                    </>
                  ) : (
                    'Cargar Más Códigos'
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className='text-center text-text-secondary'>
            No se encontraron códigos populares.
          </p>
        )}
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* FAQ Section */}
      <Faq
        title='Preguntas Frecuentes sobre Correspondencias CNAE'
        description='Resolvemos tus dudas sobre las correspondencias entre CNAE 2025 y CNAE 2009, su aplicación y cómo utilizarlas correctamente para actualizar la clasificación de tu actividad económica.'
        faqData={faqData}
      />
    </div>
  )
}

export default CorrespondenceCnaePage
